/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { Button } from "Components/Button/Button";
import { Badge } from "Components/Badge/Badge";
import { useSessionStorage } from "Hooks/useSessionStorage";
import { useQuery } from "Hooks/useQuery";
import { storageService } from "Services/storage_service";
import { paramService } from "Services/param_service";
import { getPaqueteDetalle } from "Services/paquete_cursos_service";
import { getCursoDetalle } from "Services/cursos_service";
import { getElegibilidadReferido } from "Services/referidos_service";
import { setActiveDrawer } from "../../Slices/uiSlice";
import {
  ULTIMO_CURSO,
  ULTIMO_PAQUETE,
  TIPOS_APRENDIZAJE,
  DIPLOMA_FIRMA_URL,
  HTTP_METHODS,
  HTTP_HEADERS,
} from "Constantes/constants";
import { DRAWER_TYPES } from "Constantes/drawers";
import diplomaIllustration from "Recursos/diploma.webp";
import { RUTAS } from "rutas";
import { saveAs } from "file-saver";
import { Document, Page, pdfjs } from "react-pdf";
import { CircularProgress } from "@mui/material";
import { CaptacionLeads } from "Components/CaptacionLeads/CaptacionLeads";
import "./Diploma.scss";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const Diploma = () => {
  const { isAuthLoading } = useSelector((state) => state.auth);
  const { idCurso, idPaquete } = useParams();
  const { value: ultimoCurso } = useSessionStorage(ULTIMO_CURSO);
  const { value: ultimoPaquete } = useSessionStorage(ULTIMO_PAQUETE);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [diplomaBlob, setDiplomaBlob] = useState(null);
  const [diplomaLoading, setDiplomaLoading] = useState(false);
  const [diplomaError, setDiplomaError] = useState(false);
  const [openCaptacionLeads, setOpenCaptacionLeads] = useState(false);

  const cookieUser = storageService.getItem("user");
  const isAuthenticated = Boolean(cookieUser?.idUsuario);
  const userId = isAuthenticated ? cookieUser.idUsuario : null;

  const isCurso = Boolean(idCurso);
  const isPaquete = Boolean(idPaquete);

  const { data: apiDiploma } = useQuery(paramService.param, {
    grupo: "apis",
  });

  const getParamValue = (data, codigo) => {
    if (!data || !Array.isArray(data)) return null;
    const param = data.find((p) => p.codigoParametro === codigo);
    return param?.parametro || null;
  };

  const endpointDiploma = getParamValue(apiDiploma?.data, "apiDiploma") || null;

  const { data: paqueteResponse, isLoading: isPaqueteLoading } = useQuery(
    getPaqueteDetalle,
    {
      idUsuario: userId,
      idPaquete: parseInt(idPaquete),
    },
    isPaquete
  );

  const { data: cursoResponse, isLoading: isCursoLoading } = useQuery(
    getCursoDetalle,
    {
      idUsuario: userId,
      idCurso: parseInt(idCurso),
    },
    isCurso
  );

  const { data: elegibilidadResponse, refetch: refetchElegibilidad } = useQuery(
    getElegibilidadReferido,
    { dpi: cookieUser?.usuario },
    Boolean(cookieUser?.usuario)
  );

  const isLoading = isAuthLoading || isPaqueteLoading || isCursoLoading;

  const getCurrentItem = () => {
    if (isPaquete) return paqueteResponse;
    if (isCurso) return cursoResponse;
    return null;
  };

  const handleClickRegister = () => {
    dispatch(setActiveDrawer(DRAWER_TYPES.REGISTER));
  };

  const handleDownloadDiploma = () => {
    if (diplomaBlob) {
      const currentItem = getCurrentItem();
      const fileName = `DIPLOMA ${currentItem?.titulo || "curso"}.pdf`;
      saveAs(diplomaBlob, fileName);
    }
  };

  const handleOpenCaptacionLeads = () => {
    setOpenCaptacionLeads(true);
  };

  const handleCloseCaptacionLeads = () => {
    setOpenCaptacionLeads(false);
    refetchElegibilidad();
  };

  const loadDiploma = async () => {
    try {
      if (!endpointDiploma || !isAuthenticated) return;

      setDiplomaLoading(true);
      setDiplomaError(false);

      const diplomaData = {
        dpi: cookieUser?.dpi || cookieUser?.usuario,
        idTipoAprendizaje: isPaquete
          ? TIPOS_APRENDIZAJE.PAQUETE
          : TIPOS_APRENDIZAJE.CURSO,
        idAprendizaje: isPaquete ? parseInt(idPaquete) : parseInt(idCurso),
        firma: DIPLOMA_FIRMA_URL,
      };

      const response = await fetch(endpointDiploma, {
        method: HTTP_METHODS.POST,
        headers: {
          "Content-Type": HTTP_HEADERS.CONTENT_TYPE_JSON,
        },
        body: JSON.stringify(diplomaData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      setDiplomaBlob(blob);
    } catch (error) {
      console.error("Error cargando diploma:", error);
      setDiplomaError(true);
    } finally {
      setDiplomaLoading(false);
    }
  };

  const redirectUser = useCallback(() => {
    if (isPaquete) {
      navigate(RUTAS.PAQUETE(idPaquete));
      return;
    }

    if (isCurso) {
      navigate(RUTAS.CURSO(idCurso));
    }
  }, [isCurso, idCurso, isPaquete, idPaquete, navigate]);

  const validateUnauthenticated = useCallback(() => {
    if (isPaquete) {
      const isPaqueteCompleto = ultimoPaquete?.cursos?.every(
        (curso) => curso.progreso?.porcentajeTotal === 100
      );

      if (
        Number(ultimoPaquete?.idPaquete) !== Number(idPaquete) ||
        !isPaqueteCompleto
      ) {
        navigate(RUTAS.PAQUETE(idPaquete));
        return;
      }
    }

    if (isCurso) {
      if (
        Number(ultimoCurso?.idCurso) !== Number(idCurso) ||
        ultimoCurso?.progreso?.porcentajeTotal !== 100
      ) {
        navigate(RUTAS.CURSO(idCurso));
      }
    }
  }, [
    isCurso,
    isPaquete,
    idCurso,
    idPaquete,
    ultimoCurso,
    ultimoPaquete,
    navigate,
  ]);

  const validateAuthenticated = useCallback(() => {
    if (isPaquete && paqueteResponse) {
      const porcentaje = Number(paqueteResponse?.progreso?.porcentajeTotal);
      if (porcentaje < 100) {
        navigate(RUTAS.PAQUETE(idPaquete));
        return;
      }
    }

    if (isCurso && cursoResponse) {
      const porcentaje = Number(cursoResponse?.progreso?.porcentajeTotal);
      if (porcentaje < 100) {
        navigate(RUTAS.CURSO(idCurso));
      }
    }
  }, [idCurso, idPaquete, paqueteResponse, cursoResponse]);

  useEffect(() => {
    if (!isLoading) {
      if (isPaquete && paqueteResponse === null) {
        navigate(RUTAS.PRINCIPAL);
        return;
      }

      if (isCurso && cursoResponse === null) {
        navigate(RUTAS.PRINCIPAL);
      }
    }
  }, [isLoading, paqueteResponse, cursoResponse]);

  useEffect(() => {
    if (isLoading) return;
    if (isPaquete && !paqueteResponse) return;
    if (isCurso && !cursoResponse) return;

    if (!isAuthenticated) {
      validateUnauthenticated();
    } else {
      validateAuthenticated();
      if (endpointDiploma) {
        loadDiploma();
      }
    }
  }, [
    isLoading,
    JSON.stringify(paqueteResponse),
    JSON.stringify(cursoResponse),
    endpointDiploma,
  ]);

  return (
    <Layout>
      <div className="wrapper-diploma">
        {!isAuthenticated && (
          <div className="diploma">
            <h1 className="diploma__title">¡Excelente!</h1>
            <p className="diploma__description">
              <strong>¡Tu diploma te espera!</strong>
              Inicia sesión ahora para obtenerlo.
            </p>

            <div className="diploma__card">
              <img
                src={diplomaIllustration}
                className="diploma__illustration"
                alt="Diploma illustration"
              />

              <div className="diploma__course-info">
                <span className="diploma__category">
                  <Badge
                    textColor={getCurrentItem()?.colorTexto}
                    backgroundColor={getCurrentItem()?.colorFondo}
                    borderColor={getCurrentItem()?.colorBorde}
                  >
                    {isLoading ? "Cargando..." : getCurrentItem()?.categoria}
                  </Badge>
                </span>
                <h2 className="diploma__course-title">
                  {isLoading ? "Cargando..." : getCurrentItem()?.titulo}
                </h2>
              </div>
            </div>

            <div className="w-full">
              <Button
                variant="secundario"
                className="diploma__btn"
                onClick={redirectUser}
              >
                Regresar
              </Button>
            </div>

            <p className="diploma__signup-text">¿Aún no tienes una cuenta?</p>

            <div className="w-full">
              <Button
                variant="secundario-cta"
                className="diploma__btn"
                onClick={handleClickRegister}
              >
                Crear cuenta
              </Button>
            </div>
          </div>
        )}
        {isAuthenticated && (
          <>
            <div className="diploma diploma--mobile">
              <h1 className="diploma__title">¡Felicidades!</h1>
              <p className="diploma__description">
                <strong>
                  Completaste un {isCurso ? "curso corto" : "paquete de cursos"}
                </strong>
                En Fundación Génesis Empresarial apoyamos cada uno de tus
                logros.
              </p>

              <div className="diploma__card">
                <img
                  src={diplomaIllustration}
                  className="diploma__illustration"
                  alt="Diploma illustration"
                />

                <div className="diploma__course-info">
                  <span className="diploma__category">
                    <Badge
                      textColor={getCurrentItem()?.colorTexto}
                      backgroundColor={getCurrentItem()?.colorFondo}
                      borderColor={getCurrentItem()?.colorBorde}
                    >
                      {isLoading ? "Cargando..." : getCurrentItem()?.categoria}
                    </Badge>
                  </span>
                  <h2 className="diploma__course-title">
                    {isLoading ? "Cargando..." : getCurrentItem()?.titulo}
                  </h2>
                </div>
              </div>

              <div className="w-full">
                <Button
                  variant="principal-cta"
                  className="diploma__btn"
                  onClick={handleDownloadDiploma}
                  isDisabled={isLoading || diplomaLoading || !diplomaBlob}
                >
                  {diplomaLoading && (
                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                  )}
                  Descargar diploma
                </Button>
              </div>

              <div className="w-full">
                <Button
                  variant="secundario"
                  className="diploma__btn"
                  onClick={redirectUser}
                >
                  Regresar
                </Button>
              </div>

              {elegibilidadResponse?.result && (
                <>
                  <p className="diploma__prestamos-text">
                    ¿Quieres información de nuestros préstamos?
                  </p>
                  <div className="w-full">
                    <Button
                      variant="secundario-cta"
                      className="diploma__btn"
                      onClick={handleOpenCaptacionLeads}
                    >
                      Solicitar información
                    </Button>
                  </div>
                </>
              )}
            </div>

            <div className="certificado certificado--desktop">
              <h1 className="certificado__title">
                ¡Felicidades!
                <br />
                Completaste un {isCurso ? "curso corto" : "paquete de cursos"}
              </h1>
              <p className="certificado__description">
                En Fundación Génesis Empresarial apoyamos cada uno de tus
                logros. Ayúdanos a mejorar con tus comentarios.
              </p>

              <div className="certificado__container">
                {diplomaLoading ? (
                  <div className="certificado__loading">
                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                    <p>Cargando diploma...</p>
                  </div>
                ) : diplomaBlob ? (
                  <>
                    <Document
                      file={diplomaBlob}
                      onLoadError={() => console.log("Error cargando diploma")}
                      loading={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={20} sx={{ marginRight: 1 }} />
                          <p>Cargando diploma...</p>
                        </div>
                      }
                    >
                      <Page
                        pageNumber={1}
                        width={629}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Document>
                    <div className="certificado__download-button">
                      <Button
                        variant="principal-cta"
                        onClick={handleDownloadDiploma}
                        isDisabled={diplomaLoading || !diplomaBlob}
                      >
                        Descargar diploma
                      </Button>
                    </div>
                  </>
                ) : diplomaError ? (
                  <div className="certificado__no-diploma">
                    <p>No se pudo cargar el diploma</p>
                  </div>
                ) : (
                  <div className="certificado__waiting">
                    <CircularProgress size={20} sx={{ marginRight: 1 }} />
                    <p>Preparando diploma...</p>
                  </div>
                )}
              </div>

              {elegibilidadResponse?.result && (
                <div className="certificado__prestamos-section">
                  <p className="certificado__prestamos-text">
                    ¿Quieres información de nuestros préstamos?
                  </p>
                  <Button
                    variant="secundario-cta"
                    onClick={handleOpenCaptacionLeads}
                  >
                    Solicitar información
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
      <CaptacionLeads
        open={openCaptacionLeads}
        onClose={handleCloseCaptacionLeads}
      />
    </Layout>
  );
};
